import React from 'react';
import { bool, func, oneOf } from 'prop-types';
import { Field } from 'react-final-form';
import { OnChange } from 'react-final-form-listeners';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import {
  AvailabilityBadge,
  SelectableOptionCard,
  SelectableOptionSection,
} from '@landtrustinc/design-system';
import { formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import { composeValidators } from '../../util/validators';
import {
  GLP_SELECTION,
  calculateGlpProtectionCost,
  isGlpLtPlusSelection,
  requiredGlpSelection,
} from '../../util/guestLiabilityProtection';
import { LINE_ITEM_GUEST_LIABILITY_PROTECTION, propTypes } from '../../util/types';

import { ValidationError } from '..';
import SelectableOptionCardsSkeleton from '../SelectableOptionCardsSkeleton/SelectableOptionCardsSkeleton';
import LtPlusPerksBox from './LtPlusPerksBox';
import css from './FieldGuestLiabilityProtection.module.css';

const { Money } = sdkTypes;

const calculateProtectionCost = (transaction, currency) =>
  calculateGlpProtectionCost(transaction, currency);

const glpLtPlusBadges = (
  <AvailabilityBadge variant="plus">
    <FormattedMessage id="FieldGuestLiabilityProtection.ltPlusOfferBadge" />
  </AvailabilityBadge>
);

const FieldGuestLiabilityProtectionComponent = props => {
  const {
    currentValue,
    onValueChange,
    intl,
    onApply,
    transaction,
    isLocked,
    isApplyingProtection,
    setIsApplyingProtection,
  } = props;

  const lineItems = transaction?.attributes?.lineItems || [];
  const currency = transaction?.attributes?.payinTotal?.currency || 'USD';
  const protectionLineItem = lineItems.find(
    item => item.code === LINE_ITEM_GUEST_LIABILITY_PROTECTION && !item.reversal
  );
  const protectionCost =
    protectionLineItem?.lineTotal || calculateProtectionCost(transaction, currency);
  const formattedProtectionCost = formatMoney(intl, protectionCost);
  const formattedWaivedCost = formatMoney(intl, new Money(0, currency));

  const handleSelectionChange = async (value, prevValue) => {
    if (isLocked || value === prevValue) {
      return;
    }

    try {
      setIsApplyingProtection(true);
      await onApply(value);
    } finally {
      setIsApplyingProtection(false);
    }
  };

  return (
    <SelectableOptionSection
      showDivider
      className={css.root}
      title={<FormattedMessage id="FieldGuestLiabilityProtection.protectionLabel" />}
      description={<FormattedMessage id="FieldGuestLiabilityProtection.protectionSubHeading" />}
    >
      {isApplyingProtection && !isLocked ? (
        <SelectableOptionCardsSkeleton cardCount={3} badgeCounts={[3, 0, 1]} />
      ) : isLocked ? (
        <SelectableOptionCard
          variant="plus"
          selected
          disabled
          title={<FormattedMessage id="FieldGuestLiabilityProtectionToggle.includedHeading" />}
          subtitle={
            <>
              <s>{formattedProtectionCost}</s> <strong>{formattedWaivedCost}</strong>{' '}
              <FormattedMessage id="FieldGuestLiabilityProtectionToggle.includedPricingSuffix" />
            </>
          }
        />
      ) : (
        <>
          <OnChange name="guestLiabilityProtection">
            {(value, prevValue) => {
              handleSelectionChange(value, prevValue);
            }}
          </OnChange>

          <SelectableOptionCard
            id="guestLiabilityProtectionYes"
            selected={currentValue === GLP_SELECTION.YES}
            onClick={() => onValueChange(GLP_SELECTION.YES)}
            title={
              <FormattedMessage
                id="FieldGuestLiabilityProtectionToggle.toggleYes"
                values={{ price: formattedProtectionCost }}
              />
            }
            subtitle={
              <FormattedMessage id="FieldGuestLiabilityProtectionToggle.toggleYesSubText" />
            }
          />

          <SelectableOptionCard
            id="guestLiabilityProtectionNo"
            tone="neutral"
            selected={currentValue === GLP_SELECTION.NO}
            onClick={() => onValueChange(GLP_SELECTION.NO)}
            title={<FormattedMessage id="FieldGuestLiabilityProtectionToggle.toggleNo" />}
            subtitle={<FormattedMessage id="FieldGuestLiabilityProtectionToggle.toggleNoSubText" />}
          />

          <SelectableOptionCard
            id="guestLiabilityProtectionLtPlus"
            variant="plus"
            selected={currentValue === GLP_SELECTION.LT_PLUS}
            onClick={() => onValueChange(GLP_SELECTION.LT_PLUS)}
            title={<FormattedMessage id="FieldGuestLiabilityProtection.ltPlusOfferHeading" />}
            subtitle={
              <FormattedMessage id="FieldGuestLiabilityProtection.ltPlusOfferDescription" />
            }
            footer={glpLtPlusBadges}
          />

          {isGlpLtPlusSelection(currentValue) && (
            <LtPlusPerksBox protectionCost={protectionCost} intl={intl} />
          )}
        </>
      )}
    </SelectableOptionSection>
  );
};

FieldGuestLiabilityProtectionComponent.propTypes = {
  currentValue: oneOf([GLP_SELECTION.YES, GLP_SELECTION.NO, GLP_SELECTION.LT_PLUS]),
  onValueChange: func,
  intl: intlShape.isRequired,
  onApply: func.isRequired,
  transaction: propTypes.transaction.isRequired,
  isLocked: bool,
  isApplyingProtection: bool,
  setIsApplyingProtection: func,
};

FieldGuestLiabilityProtectionComponent.defaultProps = {
  currentValue: undefined,
  onValueChange: () => {},
  isLocked: false,
  isApplyingProtection: false,
  setIsApplyingProtection: () => {},
};

const FieldGuestLiabilityProtection = props => {
  const { isLocked, intl } = props;

  return (
    <Field
      name="guestLiabilityProtection"
      validate={
        isLocked
          ? undefined
          : composeValidators(
              requiredGlpSelection(
                intl.formatMessage({ id: 'FieldGuestLiabilityProtection.protectionRequired' })
              )
            )
      }
    >
      {({ input, meta }) => (
        <>
          <FieldGuestLiabilityProtectionComponent
            currentValue={input.value}
            onValueChange={input.onChange}
            {...props}
          />
          {!isLocked && <ValidationError fieldMeta={meta} fieldName="guestLiabilityProtection" />}
        </>
      )}
    </Field>
  );
};

FieldGuestLiabilityProtection.propTypes = {
  onApply: func.isRequired,
  transaction: propTypes.transaction.isRequired,
  isLocked: bool,
  isApplyingProtection: bool,
  setIsApplyingProtection: func,
  intl: intlShape.isRequired,
};

FieldGuestLiabilityProtection.defaultProps = {
  isLocked: false,
  isApplyingProtection: false,
  setIsApplyingProtection: () => {},
};

export default injectIntl(FieldGuestLiabilityProtection);
